import { FC } from 'react';

import { ReactComponent as DeleteIcon } from '~/ui/assets/images/delete.svg';
import variables from '~/ui/assets/styles/colors.module.scss';
import ButtonIcon from '~/ui/components/common/ButtonIcon';

import AllocationClientRow from '../../../shared/components/AllocationClientRow/AllocationClientRow';
import MemoField from '../../../shared/components/MemoField/MemoField';
import TeamsField from '../../../shared/components/TeamField/TeamsField';
import { ClientWithAllocation } from '../../../shared/model/types';
import { useEditClientAllocationContext } from '../../context/EditClientAllocationContext';
import useClientAvailabilityMessages from '../../hooks/validateAllocations/useClientAvailabilityMessages';
import { IClientOptionTeam } from '~/store/client/types';

interface IProps {
  field: ClientWithAllocation & { id: string };
  clientIdx: number;
  teamMemberIdx: number;
  memberId: number;
  sameAllocationFieldsCount: number;
  onMarkChanged: (id: number) => void;
  onMarkDeleted: (id: number) => void;
  onRemove: (idx: number) => void;
  availableClientTeams?: IClientOptionTeam[];
}

const AllocationClientRowItem: FC<IProps> = ({
  field,
  clientIdx,
  teamMemberIdx,
  memberId,
  sameAllocationFieldsCount,
  onMarkChanged,
  onMarkDeleted,
  onRemove,
  availableClientTeams,
}) => {
  const MEMO_FIELD_NAME = `teamMembersAllocations.${teamMemberIdx}.clientsAllocations.${clientIdx}.memo`;
  const { alertMessage, noteMessage } = useClientAvailabilityMessages({
    clientId: field.clientId,
    memberId,
    teamMemberIdx,
  });

  const { currentUserRole } = useEditClientAllocationContext();
  const client = {
    photo: field.photo,
    name: field.name,
    isResolved: field.allocation?.resolved,
    isCrisis: field.allocation?.isCrisis,
    clientId: field.clientId,
    teams: availableClientTeams || (field.teams as unknown as IClientOptionTeam[]),
  };

  const onChangeCallback = () => {
    const changesAllocationId = field.allocation?.id || field.allocationId;

    if (changesAllocationId) {
      onMarkChanged(changesAllocationId);
    }
  };

  const handleDelete = () => {
    const allocationId = field.allocation?.id || field.allocationId;

    onRemove(clientIdx);

    if (allocationId) {
      if (sameAllocationFieldsCount < 2) {
        onMarkDeleted(allocationId);
        return;
      }
      onMarkChanged(allocationId);
    }
  };

  return (
    <AllocationClientRow
      client={client}
      alertMessage={alertMessage}
      noteMessage={noteMessage}
      memoField={
        <MemoField
          disabled={field.allocation?.resolved}
          memoFieldName={MEMO_FIELD_NAME}
          onMemoChange={onChangeCallback}
        />
      }
      teamsField={
        field.teams && currentUserRole.isGlobalUser ? (
          <TeamsField
            teams={client.teams}
            selectedTeamId={field.teamId}
            onChangeCallback={onChangeCallback}
            fieldName={`teamMembersAllocations.${teamMemberIdx}.clientsAllocations.${clientIdx}.teamId`}
          />
        ) : undefined
      }
      actions={
        <ButtonIcon disabled={field.allocation?.resolved} onClick={handleDelete}>
          <DeleteIcon width={20} color={variables.colorRed} />
        </ButtonIcon>
      }
    />
  );
};

export default AllocationClientRowItem;
